/**
 * InfoIconButton - A small circular "i" icon button for metric explanations
 * 
 * Usage:
 *   <InfoIconButton onClick={() => setShowModal(true)} />
 *   <InfoIconButton metricKey="mrr" /> // Auto-opens modal with glossary content
 */

import React, { useState } from 'react';
import { Info } from 'lucide-react';
import InfoModal from './InfoModal';
import { METRIC_GLOSSARY } from '../../content/metricGlossary';

export default function InfoIconButton({ 
  metricKey, 
  onClick, 
  className = '',
  size = 'sm' 
}) {
  const [showModal, setShowModal] = useState(false);
  
  const sizeClasses = {
    xs: 'w-4 h-4 min-w-[16px] min-h-[16px]',
    sm: 'w-5 h-5 min-w-[20px] min-h-[20px]',
    md: 'w-6 h-6 min-w-[24px] min-h-[24px]',
  };
  
  const iconSizes = {
    xs: 'w-2.5 h-2.5',
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
    } else if (metricKey) {
      setShowModal(true);
    }
  };

  const glossaryEntry = metricKey ? METRIC_GLOSSARY[metricKey] : null;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`
          inline-flex items-center justify-center rounded-full
          bg-slate-200/80 dark:bg-slate-700/60 
          hover:bg-slate-300 dark:hover:bg-slate-600
          text-slate-500 dark:text-slate-400 
          hover:text-slate-700 dark:hover:text-slate-200
          transition-colors duration-150
          focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:ring-offset-1
          ${sizeClasses[size]}
          ${className}
        `}
        aria-label="Explain this metric"
        title="What does this mean?"
        data-testid={metricKey ? `info-btn-${metricKey}` : 'info-btn'}
      >
        <Info className={iconSizes[size]} />
      </button>

      {/* Auto-modal when metricKey is provided */}
      {metricKey && (
        <InfoModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={glossaryEntry?.title || 'About this metric'}
          description={glossaryEntry?.description || ['Definition not added yet.']}
          calculation={glossaryEntry?.calculation}
          whyItMatters={glossaryEntry?.why_it_matters}
        />
      )}
    </>
  );
}
